import { useRequestCreateAccountingUser, useRequestUpdateAccountingUser } from '@/hooks/httpRequest';
import { useFormContext, useWatch } from 'react-hook-form';
import { Button, DialogFooter } from '@/components/ui';
import { AccountingUserTypeEnum, AccountingUserStatusEnum } from '@/enums';
import { broadcastService } from '@/services';
import { getApiErrorMessage, notify } from '@/functions';
import { Loader2 } from 'lucide-react';
import type { TCreateAccountingUserPayload, TUpdateAccountingUserPayload } from '@/interfaces';
import type { TFormData } from '.';

type TProps = {
    onSubmit?: (data: TFormData) => Promise<void>;
};

const FormCreateAccountingUserFooter = ({ onSubmit }: TProps) => {
    const {
        handleSubmit,
        setValue,
        control,
        formState: { isValid }
    } = useFormContext<TFormData>();
    const { trigger: triggerCreate, isLoading: isLoadingCreate } = useRequestCreateAccountingUser();
    const { trigger: triggerUpdate, isLoading: isLoadingUpdate } = useRequestUpdateAccountingUser();
    const callback = useWatch({ control, name: 'callback' });
    const readonly = useWatch({ control, name: 'readonly' });
    const _id = useWatch({ control, name: '_id' });
    const isSubmitting = useWatch({ control, name: 'isSubmitting' });

    const isLoading = isLoadingCreate || isLoadingUpdate || isSubmitting;
    const isEditMode = !!_id;

    const handleOnClickBtnCancel = () => {
        broadcastService.modal.hide();
        callback?.({ event: 'canceled' });
    };

    const handleOnClickBtnSave = () =>
        handleSubmit(async data => {
            setValue('isSubmitting', true);
            try {
                const submitData: TCreateAccountingUserPayload = {
                    name: data.name,
                    role: data.role,
                    type: data.type,
                    status: data.status,
                    ...(data.type === AccountingUserTypeEnum.EMPLOYEE
                        ? {
                            baseSalaryEur: Number(data.baseSalaryEur),
                            startedAt: data.startedAt,
                            disabledAt: data.status === AccountingUserStatusEnum.DISABLED ? data.disabledAt : undefined
                        }
                        : { percentage: Number(data.percentage) })
                };

                if (onSubmit) {
                    await onSubmit(data);
                } else if (isEditMode) {
                    if (!_id) {
                        throw new Error("Erreur lors de la modification de l'utilisateur comptable");
                    }

                    const updatePayload: TUpdateAccountingUserPayload = { ...submitData, _id };
                    const updatedAccountingUser = await triggerUpdate(updatePayload).catch(error => {
                        throw new Error(getApiErrorMessage(error, "Erreur lors de la modification de l'utilisateur comptable"));
                    });
                    callback?.({ event: 'success', data: updatedAccountingUser });
                } else {
                    const createdAccountingUser = await triggerCreate(submitData).catch(error => {
                        throw new Error(getApiErrorMessage(error, "Erreur lors de la creation de l'utilisateur comptable"));
                    });
                    callback?.({ event: 'success', data: createdAccountingUser });
                }

                notify(
                    isEditMode ? "L'utilisateur comptable a ete modifie avec succes" : "L'utilisateur comptable a ete cree avec succes"
                );
                broadcastService.modal.hide();

                if (onSubmit) {
                    callback?.({ event: 'success' });
                }
            } catch (error) {
                setValue('isSubmitting', false);
                setValue('errorMessage', (error as Error)?.message);
                callback?.({ event: 'error', error });
            }
        })();

    return (
        <DialogFooter>
            <div className="w-full flex justify-between">
                <Button onClick={handleOnClickBtnCancel} disabled={isLoading} variant="secondary" type="button" size={'sm'}>
                    {readonly ? 'Fermer' : 'Annuler'}
                </Button>
                {!readonly && (
                    <Button
                        onClick={handleOnClickBtnSave}
                        disabled={!isValid || isLoading}
                        type="button"
                        size={'sm'}
                        className="min-w-[100px]"
                    >
                        {isLoading ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                {isEditMode ? 'Modification...' : 'Creation...'}
                            </>
                        ) : isEditMode ? (
                            'Modifier'
                        ) : (
                            'Creer'
                        )}
                    </Button>
                )}
            </div>
        </DialogFooter>
    );
};

export default FormCreateAccountingUserFooter;
